import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ShieldOff, ShieldCheck, KeyRound, Lock } from "lucide-react";

const ALL_ROLES = ["worker", "approver", "admin", "tenant_admin"];
const APPROVER_UNSET = "__default__";

export default function AdminUsersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [pinTarget, setPinTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/tenant/users");
    setItems(data.items);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (u) => {
    try {
      const next = u.status === "active" ? "disabled" : "active";
      await api.patch(`/tenant/users/${u.id}`, { status: next });
      toast.success(`User ${next === "active" ? "activated" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const byId = Object.fromEntries(items.map((u) => [u.id, u]));

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administration</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Team</h1>
        <p className="mt-2 text-muted-foreground">Change roles, disable accounts, or set who reviews timesheets.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Everyone on this workspace</CardTitle>
          <Button asChild variant="outline" size="sm" data-testid="admin-users-invite-link">
            <a href="/app/admin/invites">Invite a teammate →</a>
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Approver</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>MFA</TableHead>
                <TableHead>Kiosk PIN</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">Loading…</TableCell></TableRow>}
              {items.map((u) => (
                <TableRow key={u.id} data-testid={`admin-user-row-${u.email}`}>
                  <TableCell className="font-medium">{u.full_name || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (<Badge key={r} variant="secondary" className="rounded-full">{r}</Badge>))}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {u.approver_user_id
                      ? (byId[u.approver_user_id]?.full_name || byId[u.approver_user_id]?.email || "—")
                      : <span className="italic text-xs">(workspace default)</span>}
                  </TableCell>
                  <TableCell><Badge variant={u.status === "active" ? "default" : "outline"} className="capitalize">{u.status}</Badge></TableCell>
                  <TableCell>{u.mfa_enabled ? <ShieldCheck className="h-4 w-4 text-success" /> : <ShieldOff className="h-4 w-4 text-muted-foreground" />}</TableCell>
                  <TableCell>
                    <KioskPinCell user={u} onManage={() => setPinTarget(u)} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(u)} data-testid={`edit-user-${u.email}`}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => toggleStatus(u)} data-testid={`toggle-user-${u.email}`}>
                        {u.status === "active" ? "Disable" : "Activate"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EditUserDialog user={editing} candidates={items} onClose={() => setEditing(null)} onSaved={load} />
      <KioskPinDialog user={pinTarget} onClose={() => setPinTarget(null)} onSaved={load} />
    </div>
  );
}

function KioskPinCell({ user, onManage }) {
  const has = !!user?.kiosk_pin_set;
  const locked = user?.kiosk_pin_locked_until && new Date(user.kiosk_pin_locked_until) > new Date();
  return (
    <div className="flex items-center gap-2">
      {locked ? (
        <Badge variant="outline" className="gap-1 rounded-full border-amber-500/40 text-amber-700" data-testid={`kiosk-pin-locked-${user.email}`}>
          <Lock className="h-3 w-3" /> Locked
        </Badge>
      ) : has ? (
        <Badge className="gap-1 rounded-full bg-success/15 text-success" data-testid={`kiosk-pin-set-${user.email}`}>
          <KeyRound className="h-3 w-3" /> Set
        </Badge>
      ) : (
        <Badge variant="outline" className="rounded-full text-muted-foreground" data-testid={`kiosk-pin-none-${user.email}`}>
          Not set
        </Badge>
      )}
      <Button size="sm" variant="ghost" onClick={onManage} data-testid={`manage-kiosk-pin-${user.email}`}>
        Manage
      </Button>
    </div>
  );
}

function KioskPinDialog({ user, onClose, onSaved }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) { setPin(""); setErr(""); }
  }, [user]);

  if (!user) return null;
  const locked = user?.kiosk_pin_locked_until && new Date(user.kiosk_pin_locked_until) > new Date();

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/tenant/users/${user.id}/kiosk-pin`, { pin });
      toast.success(`PIN set for ${user.email}`);
      onSaved();
      onClose();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/tenant/users/${user.id}/kiosk-pin`);
      toast.success(`PIN cleared for ${user.email}`);
      onSaved();
      onClose();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const unlock = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/tenant/users/${user.id}/kiosk-pin/unlock`);
      toast.success("Account unlocked");
      onSaved();
      onClose();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="kiosk-pin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Kiosk PIN — {user.email}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {locked && (
            <Alert variant="destructive" data-testid="kiosk-pin-locked-alert">
              <AlertDescription>
                Locked until {new Date(user.kiosk_pin_locked_until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} due to failed attempts. You can unlock immediately below.
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="pin">Set a new 6-digit PIN</Label>
            <Input
              id="pin"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              className="font-mono text-lg tracking-[0.35em]"
              data-testid="kiosk-pin-input"
            />
            <p className="text-xs text-muted-foreground">Share the PIN with the worker in person. It is stored as a bcrypt hash — nobody can read it back.</p>
          </div>
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
        </div>
        <DialogFooter className="flex flex-wrap justify-between gap-2">
          <div className="flex gap-2">
            {user.kiosk_pin_set && (
              <Button variant="outline" onClick={clear} disabled={busy} data-testid="kiosk-pin-clear-btn">
                Clear PIN
              </Button>
            )}
            {locked && (
              <Button variant="outline" onClick={unlock} disabled={busy} data-testid="kiosk-pin-unlock-btn">
                Unlock
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy || pin.length !== 6} data-testid="kiosk-pin-save-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save PIN
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, candidates, onClose, onSaved }) {
  const [full_name, setFullName] = useState("");
  const [roles, setRoles] = useState([]);
  const [approver, setApprover] = useState(APPROVER_UNSET);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || "");
      setRoles(user.roles || []);
      setApprover(user.approver_user_id || APPROVER_UNSET);
      setError("");
    }
  }, [user]);

  if (!user) return null;

  const toggle = (r) => setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);

  const save = async () => {
    setBusy(true); setError("");
    try {
      await api.patch(`/tenant/users/${user.id}`, { full_name, roles });
      // Approver is set via the dedicated endpoint
      const approver_user_id = approver === APPROVER_UNSET ? null : approver;
      if ((user.approver_user_id || null) !== approver_user_id) {
        await api.patch(`/tenant/users/${user.id}/set-approver`, { approver_user_id });
      }
      toast.success("User updated");
      onSaved();
      onClose();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const approverCandidates = (candidates || []).filter((c) => c.id !== user.id && c.status === "active");

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit {user.email}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fn">Full name</Label>
            <Input id="fn" value={full_name} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="grid grid-cols-2 gap-2">
              {ALL_ROLES.map((r) => (
                <label key={r} className="flex items-center gap-2 rounded-md border p-2 text-sm" data-testid={`edit-role-${r}`}>
                  <Checkbox checked={roles.includes(r)} onCheckedChange={() => toggle(r)} />
                  <span>{r}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Approver</Label>
            <Select value={approver} onValueChange={setApprover}>
              <SelectTrigger data-testid="edit-user-approver"><SelectValue placeholder="(workspace default)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={APPROVER_UNSET}>(workspace default)</SelectItem>
                {approverCandidates.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.full_name || c.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Who reviews their timesheets. Leave blank to inherit your workspace's default.</p>
          </div>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="edit-user-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
